import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const COLORS = [
  '#fd7f6f',
  '#7eb0d5',
  '#b2e061',
  '#bd7ebe',
  '#ffb55a',
  '#ffee65',
  '#beb9db',
  '#fdcce5',
  '#8bd3c7',
  '#ebdc78',
];

const ExperienceChart = ({ supplierId }) => {
  const [experience, setExperience] = useState([]);

  useEffect(() => {
    const abortController = new AbortController();
    let isMounted = true;

    fetch(`/rest/v2/supplier/${supplierId}/experience`, {
      signal: abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}`);
        }
        return response.json();
      })
      .then((experience) => {
        if (!isMounted) return;

        if (experience.success) {
          const mappedData = experience.data.map((yearMonth) => ({
            name: yearMonth.date,
            ...yearMonth.coordinators.reduce((memo, entry) => {
              memo[entry.name] = entry.count;
              return memo;
            }, {}),
          }));

          setExperience(mappedData);
        }
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error(error);
        }
      });

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [supplierId]);

  // Memoize coordinator keys for performance
  const coordinators = useMemo(() => {
    return Object.keys(
      experience.reduce((memo, entry) => {
        Object.keys(entry)
          .filter((key) => key !== 'name')
          .forEach((key) => {
            memo[key] = true;
          });
        return memo;
      }, {})
    );
  }, [experience]);

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart
        data={experience}
        margin={{
          top: 20,
          right: 30,
          left: 20,
          bottom: 5,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <YAxis />
        <Tooltip />
        <Legend />

        {coordinators.map((coordinator, i) => (
          <Bar
            key={coordinator}
            dataKey={coordinator}
            stackId="a"
            fill={COLORS[i] ?? '#326295'}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

export { ExperienceChart };